import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { TableCell, TableRow, Typography } from "@mui/material";

import TruncateText from "../../../components/TruncateText";
import Filters from "../../../components/Filters";
import ListFilter from "../../../components/Filters/ListFilter";
import DateRangeFilter from "../../../components/Filters/DateRangeFilter";
import SearchInput from "../../../components/SearchInput";
import Table from "../../../components/Table";
import Pagination from "../../../components/Pagination";
import EmptyState from "../../../components/EmptyState";
import Tabs from "../../../components/Tabs";
import messages from "../../../assets/locale/messages";
import emptyBoxImg from "../../../assets/images/placeholders/emptyBox.svg";
import noRequestImg from "../../../assets/images/placeholders/noProductImagePlacholder.svg";
import {
  areAllParamsEmpty,
  formatDate
} from "../../../utils/Helpers";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import {
  CATEGORY_REQUEST_STATUS,
  CATEGORY_REQUEST_STATUS_IDS
} from "../../../utils/Constants";
import {
  requestsCategoriesListRequest,
  requestsCategoriesListResponse
} from "../../../store/Categories/actions";
import { companiesLookupRequest } from "../../../store/Lookups/actions";
import { showHideLoader } from "../../../store/Loader/actions";
import "./RequestsCategories.scss";

const RequestsCategories = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLoading = useSelector((state) => state.loader.showLoader);
  const lang = useSelector((state) => state.locale.lang);
  const {
    categories: { categoriesRequests },
    shared
  } = messages[lang];

  const requestsList = useSelector(
    (state) => state.categories.requestsCategories.list
  );
  const requestsMeta = useSelector(
    (state) => state.categories.requestsCategories.meta
  );
  const companiesLookup = useSelector(
    (state) => state.lookups.companies.list
  );

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [tabStatusValue, setTabStatusValue] = useState(
    CATEGORY_REQUEST_STATUS_IDS.find(
      (status) => status.value === CATEGORY_REQUEST_STATUS.pending
    ).id
  );
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [userTypeFilterValue, setUserTypeFilterValue] = useState([]);
  const [companyFilterValue, setCompanyFilterValue] = useState([]);
  const [dateFilterValue, setDateFilterValue] = useState({
    from: null,
    to: null
  });
  const [clearFilters, setClearFilters] = useState(false);
  const [isApplyFilters, setIsApplyFilters] = useState(false);
  const [paramsObject, setParamsObject] = useState({
    userTypeIds: [],
    userIds: [],
    dateFrom: null,
    dateTo: null
  });

  const userTypesOptions = [
    { Id: 2, Name: categoriesRequests.table.exporter },
    { Id: 3, Name: categoriesRequests.table.importer },
    { Id: 4, Name: categoriesRequests.table.both }
  ];

  useEffect(() => {
    dispatch(companiesLookupRequest());
    return () => {
      dispatch(
        requestsCategoriesListResponse({
          list: [],
          meta: {}
        })
      );
    };
  }, []);

  useEffect(() => {
    dispatch(showHideLoader(true));
    let tabStatusValue = null;
    switch (tabValue) {
      case 0:
        tabStatusValue = CATEGORY_REQUEST_STATUS_IDS.find(
          (status) => status.value === CATEGORY_REQUEST_STATUS.pending
        ).id;
        break;
      case 1:
        tabStatusValue = CATEGORY_REQUEST_STATUS_IDS.find(
          (status) =>
            status.value === CATEGORY_REQUEST_STATUS.rejected
        ).id;
        break;
      default:
        break;
    }
    setClearFilters(true);
    setSearchValue("");
    setPage(1);
    setTabStatusValue(tabStatusValue);
  }, [tabValue]);

  useEffect(() => {
    if (clearFilters) {
      setUserTypeFilterValue([]);
      setCompanyFilterValue([]);
      setDateFilterValue({
        from: null,
        to: null
      });
      setParamsObject({
        userTypeIds: [],
        userIds: [],
        dateFrom: null,
        dateTo: null
      });
    }
  }, [clearFilters]);

  const requestCategoriesList = () => {
    dispatch(
      requestsCategoriesListRequest({
        params: {
          page,
          search_value: searchValue,
          categoryStatus: tabStatusValue,
          ...paramsObject
        }
      })
    );
  };

  useEffect(() => {
    requestCategoriesList();
  }, [page, searchValue]);

  useEffect(() => {
    page > 1 ? setPage(1) : requestCategoriesList();
  }, [paramsObject]);

  useEffect(() => {
    if (isApplyFilters) {
      setParamsObject({
        userTypeIds: userTypeFilterValue,
        userIds: companyFilterValue,
        dateFrom: dateFilterValue?.from || null,
        dateTo: dateFilterValue?.to || null
      });
      setIsApplyFilters(false);
    }
  }, [isApplyFilters]);

  useEffect(() => {
    setParamsObject({
      userTypeIds: [],
      userIds: [],
      dateFrom: null,
      dateTo: null
    });
  }, [tabStatusValue]);

  const onImageError = (e) => {
    e.target.src = noRequestImg;
  };

  const renderRequestTableRow = ({
    Id,
    AdminCoverImage,
    Name,
    Description,
    CompanyName,
    UserType,
    RequestDate
  }) => (
    <TableRow key={`request-row-${Id}`}>
      <TableCell>
        <div
          className={`d-flex align-items-center gap-3 ${!tabValue && "pointer"}`}
          onClick={() => {
            !tabValue &&
              navigate(
                ROUTE_PATHS.categoriesRequestsDetails.replace(
                  ":id",
                  Id
                )
              );
          }}>
          <img
            src={AdminCoverImage || noRequestImg}
            onError={onImageError}
            alt="categoryImage"
            className="category-img border-radius-8"
          />
          <TruncateText text={Name || "__"} />
        </div>
      </TableCell>
      <TableCell>
        <TruncateText text={Description || "__"} />
      </TableCell>
      <TableCell>{CompanyName || "__"}</TableCell>
      <TableCell>{UserType || "__"}</TableCell>
      <TableCell>
        {RequestDate ? formatDate(RequestDate) : "__"}
      </TableCell>
    </TableRow>
  );

  const tableHeaders = [
    categoriesRequests.table.category,
    categoriesRequests.table.description,
    categoriesRequests.table.companyName,
    categoriesRequests.table.userType,
    tabValue
      ? categoriesRequests.table.rejectDate
      : categoriesRequests.table.requestDate
  ];

  const renderTabContent = () => (
    <div>
      <div className="table-header my-4">
        <SearchInput
          name="request-search"
          id="review-request-search"
          className="review-request-search"
          value={searchValue}
          inputWrapperClass="search-input-wrapper search-input-wrapper-width"
          placeholder={categoriesRequests.searchPlaceholder}
          onChange={(value) => {
            setPage(1);
            setSearchValue(value);
          }}
        />
      </div>
      <div className="table-content">
        {requestsList?.length ? (
          <Table
            className={"px-0"}
            headlines={tableHeaders}
            rows={
              !isLoading &&
              requestsList &&
              requestsList.map((request) =>
                renderRequestTableRow(request)
              )
            }
          />
        ) : (
          <div>
            {!isLoading && (
              <EmptyState
                subTitle={
                  !!searchValue || !areAllParamsEmpty(paramsObject)
                    ? shared.noSearchResults
                    : tabValue
                      ? categoriesRequests.emptyRejectedPlaceholder
                      : categoriesRequests.emptyPlaceholder
                }
                className="empty-state-container"
                imgSrc={emptyBoxImg}
              />
            )}
          </div>
        )}
      </div>
      <div className="Pagination-container d-flex justify-content-center py-3 mt-2">
        {!isLoading && requestsList?.length > 0 && (
          <Pagination
            count={requestsMeta?.pages}
            page={requestsMeta?.currentPage || page}
            handleChange={(page) => {
              setPage(page);
            }}
            defaultPage={1}
          />
        )}
      </div>
    </div>
  );

  const renderFilterContent = () => (
    <div className="filter-content-container h-100">
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={userTypesOptions}
          name="user-types"
          label={categoriesRequests.filters.userType}
          selectedOptionsIds={userTypeFilterValue}
          setSelectedOptionsIds={setUserTypeFilterValue}
          clearFilters={clearFilters}
          collabsible
          isApplyFilters={isApplyFilters}
          filterModalOpen={filterModalOpen}
        />
      </div>
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={companiesLookup}
          name="companies"
          label={categoriesRequests.filters.companyName}
          searchPlaceholder={
            categoriesRequests.filters.searchCompanyName
          }
          selectedOptionsIds={companyFilterValue}
          setSelectedOptionsIds={setCompanyFilterValue}
          clearFilters={clearFilters}
          collabsible
          showSearchBar
          isApplyFilters={isApplyFilters}
          filterModalOpen={filterModalOpen}
        />
      </div>
      <div className="py-3 px-md-5 px-3">
        <DateRangeFilter
          name="date-range"
          label={categoriesRequests.filters.dateRange}
          onRequestFiltering={setDateFilterValue}
          clearFilters={clearFilters}
          collabsible
          selectedDateFrom={dateFilterValue?.from}
          selectedDateTo={dateFilterValue?.to}
          addBottomMargin
        />
      </div>
    </div>
  );

  const tabsArray = [
    {
      label: categoriesRequests.pending,
      content: renderTabContent()
    },
    {
      label: categoriesRequests.rejected,
      content: renderTabContent()
    }
  ];

  return (
    <div className="review-categories-list-container">
      <div className="page-title pb-4 d-flex justify-content-between align-items-center">
        <div className="title-count-container d-flex justify-content-start align-items-start align-items-md-center flex-column flex-md-row">
          <Typography variant="h5" className="me-1 title-color">
            {categoriesRequests.categoriesRequests}
          </Typography>
          <Typography className="text-secondary">{`(${requestsMeta?.count ? requestsMeta?.count : "0"} ${requestsMeta?.count === 1 ? categoriesRequests.category : categoriesRequests.categories})`}</Typography>
        </div>
        <div className="title-buttons-container d-flex justify-content-center gap-3 align-items-center">
          <Filters
            handleClearFilters={setClearFilters}
            handleApplyFilters={setIsApplyFilters}
            FilterContent={renderFilterContent()}
            setIsModalOpen={(isOpen) => setFilterModalOpen(isOpen)}
          />
        </div>
      </div>
      <div className="review-categories-table-container bg-white d-flex flex-column w-100  p-4 bg-white border-radius-8 mb-4">
        <div className="tabs-section">
          <Tabs
            tabs={tabsArray}
            value={tabValue}
            handleChange={setTabValue}
            activeTabAdornment={
              isLoading ? null : (
                <span className="records-count faint-blue-bg border-radius-50 p-2">
                  {requestsMeta?.count || 0}
                </span>
              )
            }
          />
        </div>
      </div>
    </div>
  );
};

export default RequestsCategories;
